import { ReducerCreators } from '@reduxjs/toolkit';
import { AudienceMembersSource, AudiencesState } from '../types';
import { initialState } from '../initial';
import LoadAttendees from '@/connection/audiences/all-audiences';
import type { RootState } from '@/store/root-state';
import { selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import { selectActiveChannel, selectUserChannel } from '@/store/slices/user/selectors';
import { isWebinarChannel } from '@/utils/project-types/project-type-guards';
import type { AttendeeSortDirection, AttendeeUserDefault, RegistrationFilter } from '@/types/all-attendees.types';
import {
  buildAttendeeFields,
  getDefaultColumnDefinitions,
} from '@/micros/edit-project/grid-areas/main/audiences/components/listsv2/profile-column-definitions';

/** What changed - a new source starts over from the defaults, any other filter change goes back to page 1 */
export type LoadAudienceMembersArgs = {
  source?: AudienceMembersSource;
  page?: number;
  search?: string;
  registrationStatus?: RegistrationFilter;
  sort?: { sort: string; sortDirection: AttendeeSortDirection };
};

export const loadAudienceMembers = (create: ReducerCreators<AudiencesState>) =>
  create.asyncThunk(
    // pending is dispatched before this runs, so the state read here already has the args applied
    async (_args: LoadAudienceMembersArgs, { getState, signal }) => {
      const state = getState() as RootState;
      const { source, page, pageSize, search, registrationStatus, sort, sortDirection } =
        state.audiences.audienceMembers;
      const columns = getDefaultColumnDefinitions(isWebinarChannel(selectUserChannel(state)?.type));

      return await LoadAttendees<AttendeeUserDefault>({
        channel: selectActiveChannel(state),
        eventUuid: selectWorkingProjectUuid(state)!,
        page: page - 1,
        pageSize,
        search,
        sort,
        sortDirection,
        fields: buildAttendeeFields(columns.map((column) => column.id)),
        status: source?.status,
        passcodeListId: source?.passcodeListId,
        // dynamic lists are already locked to a status, so the registration filter only applies to passcode lists
        registrationStatusFilter: source?.status ? undefined : registrationStatus,
        signal,
      });
    },
    {
      pending: (state, action) => {
        const args = action.meta.arg;
        if (args.source) {
          state.audienceMembers = { ...initialState.audienceMembers, source: args.source };
        }
        if (args.search !== undefined) {
          state.audienceMembers.search = args.search;
          state.audienceMembers.page = 1;
        }
        if (args.registrationStatus !== undefined) {
          state.audienceMembers.registrationStatus = args.registrationStatus;
          state.audienceMembers.page = 1;
        }
        if (args.sort) {
          state.audienceMembers.sort = args.sort.sort;
          state.audienceMembers.sortDirection = args.sort.sortDirection;
          state.audienceMembers.page = 1;
        }
        if (args.page !== undefined) {
          state.audienceMembers.page = args.page;
        }
        state.audienceMembers.loading = true;
        state.audienceMembers.requestId = action.meta.requestId;
      },
      fulfilled: (state, action) => {
        if (action.meta.requestId !== state.audienceMembers.requestId) return;
        state.audienceMembers.users = action.payload.data || [];
        state.audienceMembers.total = action.payload.total || 0;
        state.audienceMembers.loading = false;
      },
      rejected: (state, action) => {
        if (action.meta.requestId !== state.audienceMembers.requestId) return;
        console.error(action.error);
        state.audienceMembers.users = [];
        state.audienceMembers.total = 0;
        state.audienceMembers.loading = false;
      },
    }
  );
